import { BubbleBadge, BubbleButton, BubbleFormGroup } from './BubbleUI';
import { Event } from '@go-shared/types/event';
import BubbleTable, { BubbleTableFields } from './BubbleUI/BubbleTable';
import { DefaultedText } from './DefaultedText';
import { getStaffTime } from '@go-shared/lib/staff';
import React, { useState } from 'react';
import { truncate } from '@go-shared/lib/string';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { ReplacedDict, TranslatedReplacedDict } from '@go-shared/types/mergedVars';
import { Mcm } from '@go-shared/types/mcm';
import McmVisibilityTable from './McmVisibilityTable';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { getHourFormat } from '@go-shared/lib/property';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getBasicTimeString } from '@go-shared/lib/event';
import AudienceVisibilityTable from './AudienceVisibilityTable';
import { Audience } from '@go-shared/types/audience';
import { evaluateMcmTruthyAggregate } from '@go-shared/lib/mcm';
import BubbleMergedVarHint from './BubbleUI/BubbleMergedVarHint';

interface StaffAllocationItem {
  id: string;
  quantity: string;
  title: string;
  time: string;
  activity: string;
}

interface MergedVariableItem {
  key: string;
  label: string;
  language: string;
  value: string;
}

interface EventsGlanceProps {
  event: Event;
  title?: string;
  venue?: string;
  time?: string;
  description?: string;
  locked?: boolean;
  onUpdateEvent?: (event: Event) => void;
}

export default function EventsGlance(props: EventsGlanceProps) {
  const { defaultLanguage, mcmEndpoints } = useServerInfo();
  const { activeProperty: property } = useActiveProperty();
  const { event } = props;
  const { template, startTime, staffAllocations, mergedVars } = event;
  const [showAllDesc, setShowAllDesc] = useState(false);

  const hourFormat = getHourFormat(property);

  const saItems: StaffAllocationItem[] = staffAllocations.map(sa => ({
    id: sa._id,
    quantity: sa.quantity.toString(),
    title: sa.template.title,
    time: getStaffTime(sa, startTime, hourFormat),
    activity: sa.activity,
  }));

  const saFields: Array<BubbleTableFields<StaffAllocationItem>> = [
    { key: 'quantity' },
    { key: 'title' },
    { key: 'time', tdProps: { className: 'whitespace-nowrap' } },
    { key: 'activity' },
  ];

  staffAllocations.map(sa => ({
    id: sa._id,
    quantity: sa.quantity.toString(),
    title: sa.template.title,
    time: getStaffTime(sa, startTime, hourFormat),
    activity: sa.activity,
  }));

  const mvItems: MergedVariableItem[] = [];
  const replacements: ReplacedDict = mergedVars?.replacements || {};
  const translations: TranslatedReplacedDict = mergedVars?.translations || {};

  Object.keys(replacements).forEach(k => {
    if (replacements[k].enabled) {
      mvItems.push({
        key: `${defaultLanguage.key}-${k}`,
        label: k,
        language: defaultLanguage.key,
        value: replacements[k].value,
      });
    }
  });

  Object.keys(translations).forEach(l => {
    Object.keys(translations[l]).forEach(k => {
      if (translations[l][k].enabled) {
        mvItems.push({
          key: `${l}-${k}`,
          label: k,
          language: l,
          value: translations[l][k].value,
        });
      }
    });
  });

  const mvFields: Array<BubbleTableFields<MergedVariableItem>> = [
    { key: 'label' },
    { key: 'language' },
    { key: 'value' },
  ];

  async function onUpdateEventMcm(mcm: Mcm) {
    if (props.onUpdateEvent) {
      await props.onUpdateEvent({ ...event, mcm });
    }
  }

  async function onUpdateEventAudience(audience: Audience) {
    if (props.onUpdateEvent) {
      await props.onUpdateEvent({ ...event, audience });
    }
  }

  const isTrending = evaluateMcmTruthyAggregate(
    'trending',
    mcmEndpoints,
    event.mcm,
    event.template.mcm
  );

  return (
    <div className="grid grid-cols-1 gap-1">
      <div>
        {isTrending && (
          <BubbleBadge variant="crimson" className="mr-1">
            <FontAwesomeIcon icon="fire" className="mr-1" />
            <span>trending</span>
          </BubbleBadge>
        )}
        {template.priced && (
          <BubbleBadge variant="lime" className="mr-1">
            priced
          </BubbleBadge>
        )}
        {template.isPrivate && (
          <BubbleBadge variant="slate" className="mr-1">
            private
          </BubbleBadge>
        )}
        {template.crewOnly && (
          <BubbleBadge variant="slate" className="mr-1">
            internal only
          </BubbleBadge>
        )}
        {template.simple && (
          <BubbleBadge variant="slate" className="mr-1">
            simple
          </BubbleBadge>
        )}
        {template.inventoried && (
          <BubbleBadge variant="slate" className="mr-1">
            inventoried
          </BubbleBadge>
        )}
        {template.homecoming && (
          <BubbleBadge variant="slate" className="mr-1">
            homecoming
          </BubbleBadge>
        )}
        {template.allAboard && (
          <BubbleBadge variant="slate" className="mr-1">
            all aboard
          </BubbleBadge>
        )}
        {template.vip && (
          <BubbleBadge variant="slate" className="mr-1">
            vip
          </BubbleBadge>
        )}
        {template.notNative && (
          <BubbleBadge variant="slate" className="mr-1">
            Foreign Language
          </BubbleBadge>
        )}
        {template.archived && (
          <BubbleBadge variant="slate" className="mr-1">
            archived
          </BubbleBadge>
        )}
        {template.softConflict && (
          <BubbleBadge variant="slate" className="mr-1">
            soft conflict
          </BubbleBadge>
        )}
      </div>
      <div className="grid grid-cols-6 gap-4">
        <BubbleFormGroup label="Title" className="col-span-3">
          <BubbleMergedVarHint text={props.title || template.title} mergedVars={mergedVars} />
        </BubbleFormGroup>
        <BubbleFormGroup label="Time" className="col-span-3">
          {props.time || getBasicTimeString(event, hourFormat)}
        </BubbleFormGroup>
        <BubbleFormGroup label="Venue" className="col-span-3">
          {props.venue || event.venue.name}
        </BubbleFormGroup>
        <BubbleFormGroup label="Segment" className="col-span-3">
          {template.segment ? (
            <BubbleBadge color={template.segment.color}>{template.segment.name}</BubbleBadge>
          ) : (
            <span className="text-slate italic">None</span>
          )}
        </BubbleFormGroup>
        <BubbleFormGroup label="Levels" className="col-span-3">
          <DefaultedText val={template.levels.map(lvl => lvl.name).join(', ')} block />
        </BubbleFormGroup>
        <BubbleFormGroup label="Attributes" className="col-span-3">
          <DefaultedText val={template.attributes.map(attr => attr.name).join(', ')} block />
        </BubbleFormGroup>
        <BubbleFormGroup label="Description" className="col-span-6">
          <DefaultedText
            val={
              showAllDesc
                ? props.description || template.description
                : truncate(props.description || template.description, 100)
            }
          />
          {!showAllDesc && (
            <BubbleButton
              variant="ocean"
              inverse
              className="p-0-i ml-1"
              onClick={() => setShowAllDesc(true)}
            >
              Show All
            </BubbleButton>
          )}
        </BubbleFormGroup>
        <BubbleFormGroup className="col-span-6" label="Endpoints">
          <McmVisibilityTable
            isTemplate={false}
            readOnly={props.locked}
            sourceMcm={event.mcm}
            parentMcm={event.template.mcm}
            onUpdateMcm={onUpdateEventMcm}
          />
        </BubbleFormGroup>
        <BubbleFormGroup className="col-span-6" label="Audience">
          <AudienceVisibilityTable
            isTemplate={false}
            readOnly={props.locked}
            sourceAud={event.audience}
            parentAud={event.template.audience}
            onUpdateAudience={onUpdateEventAudience}
          />
        </BubbleFormGroup>
        <BubbleFormGroup className="col-span-6" label="Merged Variables">
          <BubbleTable
            slim
            responsive
            hideHeader
            striped
            bordered
            headVariant="navy"
            keyExtractor={e => e.key}
            stickyHeader={0}
            items={mvItems}
            fields={mvFields}
          />
        </BubbleFormGroup>
        <BubbleFormGroup className="col-span-6" label="Staff Allocations">
          <BubbleTable
            slim
            responsive
            hideHeader
            striped
            bordered
            headVariant="navy"
            keyExtractor={e => e.id}
            stickyHeader={0}
            items={saItems}
            fields={saFields}
          />
        </BubbleFormGroup>
      </div>
    </div>
  );
}
